// @flow
import React from 'react';
import { useSelector } from 'react-redux';

import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import { Field } from 'formik';

import FormikQSelect from 'components/Formik/QSelect';
import type { SelectProps as FormikSelectProps } from 'components/Formik/QSelect';
import SelectNestedMenu from 'components/SelectNestedMenu';
import { AccountTypeNodesTree } from 'data/accounts/types';
import type { AccountTypeNode } from 'data/accounts/types';
import { isAccountNodeSelectable } from 'data/accounts/utils';
import { getTopTierEntitlement } from 'data/entitlements/entitlementsSelectors';
import { getFeatureFlag } from 'utils/featureFlags/selectors';


const renderValue = (node) => node.displayName;

const useStyles = makeStyles({
  hideIcon: {
    visibility: 'hidden',
  },
});


const getMenuItemForNode = (node: AccountTypeNode, showManualTypes, currentNode, tier, showManualInvestment) => {
  if (!isAccountNodeSelectable(node, currentNode, tier)) {
    return null;
  }
  if (node.selectable && (node.connectable || showManualTypes)
    && (!showManualTypes || node.id !== 'INVESTMENT' || (showManualTypes && showManualInvestment)) // temporary until manual holdings released
  ) {
    if (node.children) {
      const childMenuItems = node.children.reduce((menuItems, child) => {
        const menuItem = getMenuItemForNode(child, showManualTypes, currentNode, tier, showManualInvestment);
        if (menuItem != null) {
          menuItems.push(menuItem);
        }
        return menuItems;
      }, []);
      return (
        <SelectNestedMenu key={node.id} MenuItemLabel={node.displayName} value={node} selectable={false}>
          {childMenuItems}
        </SelectNestedMenu>
      );
    }
    return (
      <MenuItem id={node.displayName.replace(/\s/g,'')} key={node.id} value={node}>
        {node.displayName}
      </MenuItem>
    );
  }
  return null;
};

type Props = FormikSelectProps & {
  currentAccountType: AccountTypeNode,
};

const AccountTypeSelector = (props: Props) => {
  const {
    currentAccountType,
    name = 'accountType',
    noIcon,
    showManualTypes,
    ...other
  } = props;

  const classes = useStyles();

  const tier = useSelector(getTopTierEntitlement);

  const showManualInvestment = useSelector((state) => getFeatureFlag(state, 'manualHoldings'));

  const children = AccountTypeNodesTree.reduce((menuItems, node) => {
    const menuItem = getMenuItemForNode(node, showManualTypes, currentAccountType, tier, showManualInvestment);
    if (menuItem != null) {
      menuItems.push(menuItem);
    }
    return menuItems;
  }, []);

  return (
    <Field
      classes={{ icon: noIcon ? classes.hideIcon : null }}
      component={FormikQSelect}
      {...(noIcon && { disableUnderline: true })}
      name={name}
      renderValue={renderValue}
      {...other}
    >
      {children}
    </Field>
  );
};

export default AccountTypeSelector;
